import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, DatePicker, FieldSwitch, FieldText, FlexBox, Icon, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { analytics } from 'app/utils/segment';
import { getLocalDate, isFebruary29, } from 'components/settings/components/sections/openingHours/utils/date';
import { getFormattedDate } from 'components/settings/components/sections/openingHours/views/schedule/utils/format';
import { validateIntervals } from 'components/settings/components/sections/openingHours/views/schedule/utils/validate';
import { defaultTime } from 'utils/date';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { IntervalsSelect } from '../../../intervalsSelect/intervalsSelect';
import styles from './addExceptionModal.module.scss';
function getDateAsUTC(date) {
    return moment.utc({
        year: date.year(),
        month: date.month(),
        date: date.date(),
    });
}
export const AddExceptionModal = ({ exceptions, onModalClose, onSubmit, }) => {
    var _a;
    const [date, setDate] = useState(null);
    const [isOpen, setIsOpen] = useState(true);
    const [intervals, setIntervals] = useState([
        defaultTime,
    ]);
    const [isRecurring, setIsRecurring] = useState(false);
    const [name, setName] = useState('');
    const [errors, setErrors] = useState([]);
    const { name: agentName } = useAppSelector((state) => state.settings.profile.profile);
    const handleIsOutsideRange = useCallback((day) => {
        const isReservedDate = exceptions.find(({ next_date }) => moment(next_date).isSame(day, 'day'));
        const isBeforeToday = day.isBefore(getLocalDate(), 'day');
        const isMoreThanAYear = day.isAfter(getLocalDate().add(1, 'year').subtract(1, 'day'), 'day');
        return isBeforeToday || isMoreThanAYear || isReservedDate;
    }, [exceptions]);
    const handleValidateInterval = useCallback((intervals) => void setErrors((stateErrors) => validateIntervals({
        intervals,
        dayIndex: 0,
        errors: stateErrors,
    })), []);
    const handleDateChange = useCallback(({ localDate }) => void setDate(localDate), []);
    const handleIsOpenChange = useCallback((event) => void setIsOpen(event === null || event === void 0 ? void 0 : event.target.checked), []);
    const handleIsRecurringChange = useCallback((event) => void setIsRecurring(event === null || event === void 0 ? void 0 : event.target.checked), []);
    const handleNameChange = useCallback((event) => { var _a, _b; return void setName((_b = (_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : ''); }, []);
    const hasDateAndName = !!date && !!name;
    const hasNoErrors = !((_a = errors[0]) === null || _a === void 0 ? void 0 : _a.some((e) => !!e));
    const isValid = useMemo(() => !!(hasDateAndName && (!isOpen || hasNoErrors)), [hasDateAndName, hasNoErrors, isOpen]);
    const handleSubmit = useCallback(() => {
        analytics.businessHoursScheduleAddExceptionFormSubmitted();
        if (date) {
            const startDateAsUTC = getDateAsUTC(date);
            onSubmit({
                created_at: moment().toISOString(),
                created_by: agentName,
                duration: 1,
                id: uuid(),
                intervals: isOpen ? intervals : [],
                name,
                next_date: startDateAsUTC.toISOString(),
                opening_hour_type: 'exception',
                recurring: isRecurring
                    ? {
                        day_of_month: parseInt(startDateAsUTC.format('D')),
                        month_of_year: parseInt(startDateAsUTC.format('M')),
                    }
                    : null,
                start_date: startDateAsUTC.toISOString(),
            });
            onModalClose();
        }
    }, [
        agentName,
        date,
        intervals,
        isRecurring,
        isOpen,
        name,
        onModalClose,
        onSubmit,
    ]);
    const handleModalClose = useCallback(() => {
        analytics.businessHoursScheduleAddExceptionFormCancelled();
        onModalClose();
    }, [onModalClose]);
    return (_jsxs(Modal, Object.assign({ className: styles.modal, isOpen: true, onClose: handleModalClose }, { children: [_jsx(ModalHead, { onClose: handleModalClose, title: "Add exception" }), _jsxs(ModalBody, Object.assign({ className: styles.modalBody }, { children: [useMemo(() => (_jsx(DatePicker, { calendarProps: {
                            daySize: 50,
                            isOutsideRange: handleIsOutsideRange,
                        }, minDate: moment().add(-1, 'days'), date: date, onChange: handleDateChange })), [date, handleDateChange, handleIsOutsideRange]), !!date && (_jsxs(Box, Object.assign({ className: styles.details }, { children: [_jsxs(FlexBox, Object.assign({ flexDirection: "column", margin: "0 0 400", gap: "250" }, { children: [_jsxs(Box, Object.assign({ className: styles.intervalsBox }, { children: [_jsxs("div", Object.assign({ className: styles.toggleBox }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "1" }, { children: getFormattedDate(getDateAsUTC(date).toISOString()) })), _jsx(FieldSwitch, { className: styles.fieldSwitch, input: {
                                                            name: 'is-open',
                                                            value: '',
                                                            checked: isOpen,
                                                            onChange: handleIsOpenChange,
                                                        } })] })), isOpen && (_jsx(IntervalsSelect, { onChange: setIntervals, isClosed: false, intervals: intervals, errors: errors[0] || [], validate: handleValidateInterval }))] })), _jsx(Box, Object.assign({ className: styles.recurringSettings }, { children: _jsxs(FlexBox, Object.assign({ justifyContent: "space-between", alignItems: "center" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "1" }, { children: "Repeat annually on this date" })), _jsx(FieldSwitch, { className: styles.fieldSwitch, input: {
                                                        name: 'is-recurring',
                                                        value: '',
                                                        checked: isRecurring,
                                                        onChange: handleIsRecurringChange,
                                                    } })] })) })), date && isFebruary29(date) && (_jsxs(FlexBox, Object.assign({ gap: "200" }, { children: [_jsx(Icon, { name: "exclamation-circle-filled", color: typography.color.error }), _jsx(Text.Paragraph, Object.assign({ variant: "4", color: typography.color.error }, { children: "This exception repeats every 4 years on 29 February." }))] })))] })), _jsx(FieldText, { input: {
                                    name: 'exception-name',
                                    value: name,
                                    onChange: handleNameChange,
                                }, label: "Name", noAlert: true, placeholder: "E.g. special sale, offsite, partial day closure, etc.", required: true })] })))] })), _jsx(ModalFoot, { cancelOnClick: handleModalClose, confirmName: "Add", confirmOnClick: handleSubmit, confirmProps: { disabled: !isValid } })] })));
};
